import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

const TicketPrint = ({ ticket, onClose }) => {
  const { t } = useTranslation();
  const direction = localStorage.getItem("direction") || "ltr";

  useEffect(() => {
    if (typeof document === 'undefined') return;
    
    const printStyles = `
      @media print {
        @page {
          size: 95mm auto;
          margin: 0;
        }
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        html, body {
          margin: 0;
          padding: 0;
          width: 95mm;
          height: auto;
          overflow: visible;
        }
        body * {
          visibility: hidden;
        }
        #printable, #printable * {
          visibility: visible;
        }
        #printable {
          position: absolute;
          left: 0;
          top: 0;
          width: 95mm !important;
          max-width: 95mm !important;
          margin: 0 !important;
          padding: 18px !important;
          font-family: Arial, sans-serif !important;
          font-size: 15px !important;
          line-height: 1.5 !important;
          color: #000 !important;
          background: white !important;
          text-align: left !important;
          border: none !important;
          box-shadow: none !important;
          border-radius: 0 !important;
        }
      }
      @media screen {
        #printable {
          width: 420px;
          max-width: 95vw;
          margin: 0;
          margin-right: auto;
          padding: 24px;
          font-family: Arial, sans-serif;
          font-size: 15px;
          line-height: 1.5;
          color: #000;
          background: white;
          box-shadow: 0 4px 20px rgba(0,0,0,0.12);
          border: 2px solid #000;
          border-radius: 8px;
          text-align: left;
        }
      }
    `;
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = printStyles;
    document.head.appendChild(styleSheet);

    // Auto-print when component mounts
    const timer = setTimeout(() => {
      window.print();
      if (onClose) {
        setTimeout(() => onClose(), 1000);
      }
    }, 500);

    return () => {
      clearTimeout(timer);
      if (document.head.contains(styleSheet)) {
        document.head.removeChild(styleSheet);
      }
    };
  }, [onClose]);

  if (!ticket) {
    return null;
  }

  return (
    <div
      id="printable"
      style={{
        width: "420px",
        maxWidth: "95vw",
        fontFamily: "Arial, sans-serif",
        padding: "24px",
        boxSizing: "border-box",
        direction: direction,
        color: "#000",
        margin: 0,
        marginRight: "auto",
        textAlign: "left",
        border: "2px solid #1e40af",
        borderRadius: "8px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
      }}
    >
      {/* Header Section - Centered, dark for thermal */}
      <div style={{ marginBottom: "18px", textAlign: "center" }}>
        <h2
          style={{
            margin: "0 0 8px 0",
            fontSize: "24px",
            fontWeight: "800",
            color: "#000",
            letterSpacing: "0.02em",
            lineHeight: 1.25,
          }}
        >
          {t("print.turkishClinic")}
        </h2>
        <p style={{ margin: "6px 0", fontSize: "14px", fontWeight: "700", color: "#000" }}>
          {t("print.phone")}: {t("print.phoneNumber")}
        </p>
        <p style={{ fontSize: "13px", margin: "6px 0", color: "#111", lineHeight: 1.4 }}>
          {t("print.address")}
        </p>
        <hr style={{ border: "1px dashed #000", margin: "16px 0" }} />
      </div>

      {/* Ticket & Patient Details - Dark for thermal */}
      <div
        style={{
          fontSize: "15px",
          lineHeight: "1.7",
          color: "#000",
          padding: "12px 0",
        }}
      >
        <div
          style={{
            marginBottom: "12px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            padding: "8px 10px",
            border: "2px solid #000",
            borderRadius: "4px",
          }}
        >
          <strong style={{ fontSize: "15px", color: "#000" }}>
            {t("print.visitTicketNumber", { defaultValue: "Visit Ticket Number" })}:
          </strong>
          <span style={{ fontWeight: "700", fontSize: "16px", color: "#000" }}>
            {ticket.ticket_number || "N/A"}
          </span>
        </div>
        {ticket.patient_mrn_display && (
          <div
            style={{
              marginBottom: "12px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
            }}
          >
            <strong style={{ fontSize: "15px", minWidth: "40%" }}>
              {t("print.mrn", { defaultValue: "MRN" })}:
            </strong>
            <span style={{ fontWeight: "700" }}>{ticket.patient_mrn_display}</span>
          </div>
        )}
        <div
          style={{
            marginBottom: "12px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          <strong style={{ fontSize: "15px", minWidth: "40%" }}>
            {t("print.patientName")}:
          </strong>
          <span style={{ fontWeight: "600", fontSize: "15px" }}>
            {ticket.patient_name_display || "N/A"}
          </span>
        </div>
        {ticket.patient_father_name_display && (
          <div
            style={{
              marginBottom: "12px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
            }}
          >
            <strong style={{ fontSize: "15px", minWidth: "40%" }}>
              {t("print.fatherName", { defaultValue: "Father's Name" })}:
            </strong>
            <span style={{ fontWeight: "500" }}>{ticket.patient_father_name_display}</span>
          </div>
        )}
        {ticket.patient_phone_display && (
          <div
            style={{
              marginBottom: "12px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
            }}
          >
            <strong style={{ fontSize: "15px", minWidth: "40%" }}>{t("print.phone")}:</strong>
            <span style={{ fontWeight: "500" }}>{ticket.patient_phone_display}</span>
          </div>
        )}
        {ticket.patient_age_display && (
          <div
            style={{
              marginBottom: "12px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
            }}
          >
            <strong style={{ fontSize: "15px", minWidth: "40%" }}>{t("print.age")}:</strong>
            <span style={{ fontWeight: "500" }}>{ticket.patient_age_display}</span>
          </div>
        )}
        <div
          style={{
            marginBottom: "12px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          <strong style={{ fontSize: "15px", minWidth: "40%" }}>{t("print.gender")}:</strong>
          <span style={{ fontWeight: "500" }}>{ticket.patient_gender_display || "N/A"}</span>
        </div>
        <div
          style={{
            marginBottom: "12px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          <strong style={{ fontSize: "15px", minWidth: "40%" }}>{t("print.date")}:</strong>
          <span style={{ fontWeight: "600" }}>
            {moment(ticket.visit_date).format("YYYY-MM-DD HH:mm")}
          </span>
        </div>
        {ticket.doctor_name_display && (
          <div
            style={{
              marginBottom: "12px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
            }}
          >
            <strong style={{ fontSize: "15px", minWidth: "40%" }}>
              {t("print.doctor", { defaultValue: "Doctor" })}:
            </strong>
            <span style={{ fontWeight: "500" }}>{ticket.doctor_name_display}</span>
          </div>
        )}
        {ticket.queue_number && (
          <div
            style={{
              marginBottom: "12px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
            }}
          >
            <strong style={{ fontSize: "15px", minWidth: "40%" }}>
              {t("print.inLine", { defaultValue: "In line" })}:
            </strong>
            <span style={{ fontWeight: "700", fontSize: "16px", color: "#000" }}>
              {ticket.queue_number}
            </span>
          </div>
        )}
        {ticket.department_name_display && (
          <div
            style={{
              marginBottom: "12px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
            }}
          >
            <strong style={{ fontSize: "15px", minWidth: "40%" }}>
              {t("print.department", { defaultValue: "Department" })}:
            </strong>
            <span style={{ fontWeight: "500" }}>{ticket.department_name_display}</span>
          </div>
        )}
        <div
          style={{
            marginBottom: "12px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          <strong style={{ fontSize: "15px", minWidth: "40%" }}>
            {t("print.status", { defaultValue: "Status" })}:
          </strong>
          <span style={{ fontWeight: "700", color: "#000" }}>
            {ticket.status_display || "Active"}
          </span>
        </div>
      </div>

      {/* Footer Section - Centered, dark for thermal */}
      <hr style={{ border: "1px dashed #000", margin: "16px 0" }} />
      <div style={{ textAlign: "center", color: "#000" }}>
        <p
          style={{
            fontSize: "14px",
            margin: "10px 0",
            fontWeight: "700",
            lineHeight: 1.45,
            color: "#000",
          }}
        >
          {t("print.thankYou")}
        </p>
        <p style={{ fontSize: "15px", margin: "10px 0", fontWeight: "700", color: "#000" }}>
          {t("print.getWellSoon")}
        </p>
        <p style={{ fontSize: "12px", margin: "10px 0 0 0", color: "#111", fontWeight: "600" }}>
          {t("print.cosmos")}
        </p>
      </div>
    </div>
  );
};

export default TicketPrint;
